import React, { useState, useMemo, useRef, useEffect, lazy, Suspense } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { httpClient } from "../../HttpClient";
import { useClients, useClient } from "./useClients.js";
import Create from "./Create";
import { AiOutlineDelete } from "react-icons/ai";
import ExportToExcel from "./ExportToExcel";
import { useEtatGlobal } from "../EtatGlobal.js";
import Mission from "./Mission.js";
import NoteRDV from "./NoteRDV.js";
import { IoIosSearch } from "react-icons/io";
import { GoPencil } from "react-icons/go";
// MYalfa link
import { dialog } from "../../components/ui/Dialog";
import { showSuccessToast } from "../../components/ui/Toast";
import FicheClientOverlay from "./FicheClientOverlay";
import FormOverlay from "../../components/ui/FormOverlay";
import NouveauContactForm from "./kyc-lab/organisms/NouveauContactForm";
import PlateformeLogo from "./PlateformeLogo";
import {
  ChevronDown,
  Plus,
  AlertTriangle,
  X,
  Ban,
  Archive,
} from "lucide-react";
import { computeConformite } from "./kyc-lab/conformite";
import GiLabIcon from "./kyc-lab/atoms/GiLabIcon";
import GiLabOverlaySkeleton from "./kyc-lab/atoms/GiLabOverlaySkeleton";
import { MarqueSecret, estSecret } from "./DossierSecret";

// Chargé à la demande : cet écran n'est ouvert que depuis le Portefeuille et
// n'est jamais visible au premier affichage.
// Attention avant d'en ajouter d'autres ici : FicheClientOverlay et Create
// sont importés statiquement par d'autres écrans (les 10 Suivis/Annexe pour
// la première, Home/Admin/LMLead/clientSearch pour la seconde). Les passer en
// lazy ICI ne les sort donc pas du bundle initial — ça les y duplique.
// Mesuré : bundle principal inchangé, total JS +0,74 Mo. Pour qu'un lazy
// serve, il faut convertir TOUS les points d'entrée du module.
// Le code est préchargé dès que la page est au repos (et au survol de
// l'icône) : au clic, la fenêtre s'ouvre sans attendre le téléchargement ;
// sinon la coque GiLabOverlaySkeleton s'affiche instantanément à sa place.
const chargerGiLabOverlay = () => import("./kyc-lab/GiLabOverlay.js");
const GiLabOverlay = lazy(chargerGiLabOverlay);

// Colonnes filtrables situées avant "GI / LAB" dans le tableau — sert au
// skeleton de chargement (mêmes en-têtes que le vrai tableau).
const SKELETON_COLONNES_AVANT_GILAB = [
  "formeSociale",
  "dateCloture",
  "regimeFiscal",
  "regimeTVA",
  "expertComptable",
  "collaborateur",
  "collaborateur_niv2",
  "missionComptable",
  "missionSociale",
  "missionJuridique",
];

// Barre de texte du skeleton : même hauteur de ligne qu'un texte du tableau
// (h-4) pour que les lignes aient la hauteur des vraies lignes.
function SkBar({ w }) {
  if (!w) return <div className="h-4" />;
  return (
    <div className="h-4 flex items-center">
      <div
        className="h-2.5 rounded bg-slate-200 animate-pulse"
        style={{ width: w }}
      />
    </div>
  );
}

// Badge rouge "Bloqué" (dossier bloqué pour litige de recouvrement) — même
// couleur que le badge de la fiche client.
function BadgeBloque() {
  return (
    <span
      title="Dossier bloqué"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 text-[9px] font-semibold leading-none flex-shrink-0"
    >
      <Ban className="w-2.5 h-2.5" />
      Bloqué
    </span>
  );
}

// Badge discret "Archivé" (dossier archivé affiché dans la liste principale).
function BadgeArchive() {
  return (
    <span
      title="Dossier archivé"
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200 text-[9px] font-semibold leading-none flex-shrink-0"
    >
      <Archive className="w-2.5 h-2.5" />
      Archivé
    </span>
  );
}

// Badge de statut d'une ligne : "Bloqué" (rouge) ou "Archivé" (gris).
function BadgeStatut({ status }) {
  if (status === "Bloqué") return <BadgeBloque />;
  if (status === "Archivé") return <BadgeArchive />;
  return null;
}

// Les dossiers archivés et bloqués restent dans la liste principale, mais leur
// ligne est un peu atténuée (sans être criarde) pour les repérer d'un coup d'œil.
const classeLigneStatut = (status) =>
  status === "Archivé" || status === "Bloqué" ? "bg-slate-50 opacity-70" : "";

function Suivi() {
  const {
    data: clients = [],
    isLoading: clientsLoading,
    isFetching: clientsFetching,
  } = useClients();
  const queryClient = useQueryClient();
  const [showCreateOverlay, setShowCreateOverlay] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const { utilisateur, annuaireData, updateAnnuaireContact } = useEtatGlobal();
  const [showMissionPopup, setShowMissionPopup] = useState(false);
  const [showNoteRDVPopup, setShowNoteRDVPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Vues "Archives" / "Bloqués" : n'affichent que les dossiers archivés /
  // bloqués (exclusives entre elles). La liste principale, elle, affiche tous
  // les dossiers, archivés et bloqués compris (lignes atténuées + badge).
  const [showBlocked, setShowBlocked] = useState(false);
  const [showGiLabOverlay, setShowGiLabOverlay] = useState(false);
  const [giLabClient, setGiLabClient] = useState(null);
  const [giLabProgress, setGiLabProgress] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 30;

  // Avancement GI/LAB de chaque dossier — règle de calcul unique du Centre
  // de contrôle conformité (kyc-lab/conformite.js, identique au tableau de
  // bord). Une valeur poussée par le Centre ouvert (onOverallChange) reste
  // prioritaire : elle tient compte des dernières actions faites dedans.
  useEffect(() => {
    if (!clients.length) return;
    const map = {};
    clients.forEach((client) => {
      const c = computeConformite(client);
      map[client.id] = { overall: c.overall, statut: c.statut, nbOk: c.nbOk };
    });
    setGiLabProgress((prev) => ({ ...map, ...prev }));
  }, [clients]);

  // Préchargement du Centre de contrôle conformité quand le navigateur est
  // au repos (après le chargement initial de la plateforme).
  useEffect(() => {
    const precharger = () => chargerGiLabOverlay().catch(() => {});
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(precharger, { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    }
    const t = setTimeout(precharger, 1500);
    return () => clearTimeout(t);
  }, []);

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayInitialTab, setOverlayInitialTab] = useState("general");

  // Détail complet du client sélectionné, mis en cache par React Query (plus
  // besoin de ficheClientCache local) ; en attendant qu'il arrive, on affiche
  // la version "liste" déjà en mémoire pour un affichage instantané.
  const {
    data: selectedClientDetail,
    isLoading: detailLoading,
    isFetching: detailFetching,
  } = useClient(selectedClientId);
  // Le détail arrive par tranches (voir useClient) : on le superpose à la
  // version "liste" pour que les champs pas encore reçus (ex. todos, KYC)
  // restent affichés au lieu de disparaître le temps du chargement.
  const selectedListClient = clients.find((c) => c.id === selectedClientId);
  const selectedClient = useMemo(
    () =>
      selectedClientDetail || selectedListClient
        ? { ...(selectedListClient || {}), ...(selectedClientDetail || {}) }
        : null,
    [selectedClientDetail, selectedListClient],
  );

  // Client du Centre de contrôle conformité, superposé à sa version du cache
  // partagé : une modification de la fiche faite pendant que le Centre est
  // ouvert (pays, code NAF… depuis le module KYC) s'y voit tout de suite. Les
  // tâches cochées dans le Centre (onTodoToggled) restent prioritaires.
  const giLabClientLive = useMemo(() => {
    if (!giLabClient) return null;
    const live = clients.find((c) => c.id === giLabClient.id);
    return live
      ? { ...giLabClient, ...live, todos: giLabClient.todos ?? live.todos }
      : giLabClient;
  }, [giLabClient, clients]);

  const [editingGroupementContactId, setEditingGroupementContactId] =
    useState(null);

  const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });

  const filterRef = useRef(null);

  const handleUpdateUserInList = (updatedUser) => {
    queryClient.setQueryData(["client", updatedUser.id], updatedUser);
    queryClient.setQueryData(["clients"], (prev) =>
      Array.isArray(prev)
        ? prev
            .map((c) => (c.id === updatedUser.id ? updatedUser : c))
            .sort((a, b) => a.id - b.id)
        : prev
    );
  };

  // Fonction utilitaire pour formater la date en "jj/mm"
  const formatDateWithoutYear = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    return `${day}/${month}`;
  };

  // Calculer les valeurs uniques pour chaque colonne
  const uniqueValues = useMemo(() => {
    const values = {
      formeSociale: Array.from(
        new Set(clients.flatMap((client) => client.formeSociale))
      ),

      dateCloture: Array.from(
        new Set(
          clients
            .map((client) =>
              client.dateCloture
                ? formatDateWithoutYear(client.dateCloture)
                : null
            )
            .filter(Boolean)
        )
      ),

      regimeFiscal: Array.from(
        new Set(clients.map((client) => client.regimeFiscal))
      ),
      regimeTVA: Array.from(new Set(clients.map((client) => client.regimeTVA))),
      expertComptable: Array.from(
        new Set(clients.map((client) => client.expertComptable))
      ),
      superviseur: Array.from(
        new Set(clients.map((client) => client.superviseur))
      ),
      collaborateur: Array.from(
        new Set(clients.map((client) => client.collaborateur))
      ),
      collaborateur_niv2: Array.from(
        new Set(clients.map((client) => client.collaborateur_niv2))
      ),
      missionComptable: Array.from(
        new Set(clients.map((client) => client.missionComptable))
      ),
      missionSociale: Array.from(
        new Set(clients.map((client) => client.missionSociale))
      ),
      missionJuridique: Array.from(
        new Set(clients.map((client) => client.missionJuridique))
      ),
      groupement: Array.from(
        new Set(clients.map((client) => client.groupement))
      ),
      plateformeElectronique: Array.from(
        new Set(clients.map((client) => client.plateformeElectronique))
      ),
    };
    return values;
  }, [clients]);

  const [columnFilters, setColumnFilters] = useState({
    formeSociale: [],
    dateCloture: [],
    regimeFiscal: [],
    regimeTVA: [],
    taxeSalaires: [],
    expertComptable: [],
    superviseur: [],
    collaborateur: [],
    collaborateur_niv2: [],
    missionComptable: [],
    missionSociale: [],
    missionJuridique: [],
    plateformeElectronique: [],
  });

  const [activeFilterColumn, setActiveFilterColumn] = useState(null); // colonne dont l'overlay est ouvert

  // Mémoïsé : ce filtre parcourt les ~800 dossiers et, pour chacun, les 12
  // filtres de colonnes. Sans mémoïsation il était rejoué à CHAQUE rendu,
  // donc à chaque frappe dans la recherche et à chaque ouverture d'overlay.
  // formatDateWithoutYear n'est pas en dépendance : c'est un formateur pur,
  // redéfini à chaque rendu, l'inclure annulerait la mémoïsation.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredClients = useMemo(() => clients.filter((client) => {
    return (
      // 👉 filtre Archivés / Bloqués (liste principale : tous les dossiers)
      (showArchived
        ? client.status === "Archivé"
        : showBlocked
          ? client.status === "Bloqué"
          : true) &&
      // 👉 tes filtres colonnes
      Object.entries(columnFilters).every(([col, values]) => {
        if (!values.length) return true;

        let clientValue = client[col];

        if (col === "dateCloture" && clientValue) {
          clientValue = formatDateWithoutYear(clientValue);
        }

        const clientValuesArray = Array.isArray(clientValue)
          ? clientValue
          : [clientValue];

        return clientValuesArray.some((val) => values.includes(val));
      }) &&
      // 👉 filtre recherche nom
      (nameFilter
        ? client.nom?.toLowerCase().includes(nameFilter.toLowerCase())
        : true)
    );
  }), [clients, showArchived, showBlocked, columnFilters, nameFilter]);

  // const filteredClients = clients.filter((client) => {
  //   if (!Array.isArray(filterRows) || (filterRows.length === 0 && !nameFilter))
  //     return true;

  //   return (
  //     filterRows.every(({ column, selectedValues }) => {
  //       if (!Array.isArray(selectedValues) || selectedValues.length === 0)
  //         return true;

  //       const value = client[column];

  //       if (Array.isArray(value)) {
  //         // value est un tableau
  //         return value.some((val) => selectedValues.includes(val));
  //       } else if (
  //         typeof value === "string" ||
  //         typeof value === "number" ||
  //         typeof value === "boolean"
  //       ) {
  //         // value est une primitive simple
  //         return selectedValues.includes(value);
  //       } else {
  //         // value est undefined/null/objet non géré
  //         return false;
  //       }
  //     }) &&
  //     (nameFilter
  //       ? client.nom?.toLowerCase().includes(nameFilter.toLowerCase())
  //       : true)
  //   );
  // });

  // Nombre affiché dans le titre : dans la liste principale, seuls les dossiers
  // actifs comptent (ni archivés, ni bloqués) ; dans les vues Archives /
  // Bloqués, le nombre de dossiers de la vue.
  const nbTitre =
    showArchived || showBlocked
      ? filteredClients.length
      : filteredClients.filter(
          (c) => c.status !== "Archivé" && c.status !== "Bloqué"
        ).length;

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const names = fullName.split(" ");
    return names.map((name) => name.charAt(0).toUpperCase()).join("");
  };

  const handleCheckboxChange = (clientId) => {
    setSelectedClients((prevSelected) =>
      prevSelected.includes(clientId)
        ? prevSelected.filter((id) => id !== clientId)
        : [...prevSelected, clientId]
    );
  };

  const handleSelectClient = (client) => {
    setSelectedClientId(client.id);
    setOverlayInitialTab("general");
    setShowOverlay(true);
  };

  // Clic sur le logo PA d'une ligne : ouvre directement la fiche client sur
  // l'onglet "Comptes" (là où vit désormais le type "Solution de gestion /
  // PA") plutôt que "Général" — stopPropagation requis côté appelant pour ne
  // pas déclencher aussi le onClick de la ligne.
  const handleSelectClientPlateforme = (client) => {
    setSelectedClientId(client.id);
    setOverlayInitialTab("compte");
    setShowOverlay(true);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allClientIds = filteredClients.map((client) => client.id);
      setSelectedClients(allClientIds);
    } else {
      setSelectedClients([]);
    }
  };

  const deleteClient = async (idClient) => {
    try {
      const response = await httpClient.delete(
        `https://backend-myalfa.vercel.app/api/clients/${idClient}`
      );
      return response.status === 200 || response.status === 204;
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      return false;
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedClients.length || isDeleting) return;

    // const editableIds = selectedClients.filter((id) => {
    //   const client = clients.find((c) => c.id === id);
    //   return canEditClient(utilisateur, client?.cabinet?.id ?? null);
    // });
    const editableIds = selectedClients;

    // if (!editableIds.length) {
    //   dialog.alert("Vous n'avez pas les droits pour supprimer ces clients.");
    //   return;
    // }

    setIsDeleting(true);

    try {
      const confirmDelete = await dialog.confirm(
        `Êtes-vous sûr de vouloir supprimer ${
          editableIds.length > 1
            ? `les ${editableIds.length} clients sélectionnés`
            : "le client sélectionné"
        } ?`
      );

      if (!confirmDelete) {
        setIsDeleting(false);
        return;
      }

      let deletionResults = await Promise.allSettled(
        editableIds.map((id) => deleteClient(id))
      );

      const successfulIds = editableIds.filter(
        (_, i) => deletionResults[i].status === "fulfilled" && deletionResults[i].value
      );
      const successfulDeletions = successfulIds.length;

      queryClient.setQueryData(["clients"], (prev) =>
        Array.isArray(prev) ? prev.filter((c) => !successfulIds.includes(c.id)) : prev
      );
      setSelectedClients([]);

      if (successfulDeletions === editableIds.length) {
        console.log("Tous les clients ont été supprimés avec succès");
      } else {
        console.log(
          `${successfulDeletions}/${editableIds.length} suppressions réussies`
        );
      }
    } catch (error) {
      console.error("Erreur lors de la suppression multiple:", error);
      dialog.alert("Une erreur est survenue");
    } finally {
      setIsDeleting(false);
    }
  };

  const selectedClientData = clients
    .filter((client) =>
      Array.isArray(selectedClients)
        ? selectedClients.includes(client.id)
        : false
    )
    .map((client) => ({
      Nom: client.nom,
      Groupement: client.annuaires?.[0]
        ? `${client.annuaires[0].nom || ""} ${
            client.annuaires[0].prenoms || ""
          }`.trim()
        : "",
      "Forme Sociale": Array.isArray(client.formeSociale)
        ? client.formeSociale.join(", ")
        : client.formeSociale,

      "Date Clôture": formatDateWithoutYear(client.dateCloture),
      "Régime Fiscal": client.regimeFiscal,
      "Régime TVA": client.regimeTVA,
      "Expert Comptable": client.expertComptable,
      Superviseur: client.superviseur,
      Collaborateur: client.collaborateur,
      "Collaborateur niv 2": client.collaborateur_niv2,
      "Mission Comptable": client.missionComptable,
      "Mission Sociale": client.missionSociale,
      "Mission Juridique": client.missionJuridique,
    }));

  // Groupement = les contacts de l'Annuaire liés au client (mêmes liens que
  // les "dossiers liés" affichés dans Annuaire.js — voir Update.js). Un
  // client peut avoir plusieurs contacts liés (bénéficiaires effectifs) ;
  // pour le regroupement du tableau on retient le premier comme repère de
  // groupe. On affiche son nom/prénom à jour (annuaireData, global) plutôt
  // que le snapshot embarqué sur le client, pour que le libellé du groupe
  // reflète immédiatement un renommage du contact fait ailleurs dans l'app.
  // Mémoïsé : ne se recalcule que quand la liste filtrée change.
  const { groupedClients, clientsSansGroupement } = useMemo(() => {
    const groupes = {};
    const isoles = [];

    filteredClients.forEach((client) => {
      const ann = client.annuaires?.[0];
      if (ann) {
        if (!groupes[ann.id]) {
          groupes[ann.id] = { annuaireId: ann.id, clients: [] };
        }
        groupes[ann.id].clients.push(client);
      } else {
        isoles.push(client);
      }
    });

    // Un regroupement n'est utile que si le contact est réellement partagé
    // par au moins 2 dossiers — sinon on l'affiche comme un client normal,
    // sans en-tête de groupe.
    Object.keys(groupes).forEach((annuaireId) => {
      if (groupes[annuaireId].clients.length < 2) {
        isoles.push(...groupes[annuaireId].clients);
        delete groupes[annuaireId];
      }
    });

    return { groupedClients: groupes, clientsSansGroupement: isoles };
  }, [filteredClients]);

  // Client-side pagination
  // Mémoïsé aussi : le tri par localeCompare sur tous les dossiers était
  // l'opération la plus chère du rendu, rejouée à chaque frappe alors que
  // seules 30 lignes sont affichées. L'index par id évite en plus un find()
  // linéaire dans annuaireData pour chaque groupe.
  const allItems = useMemo(() => {
    const contactsParId = new Map((annuaireData || []).map((a) => [a.id, a]));
    const libelleGroupe = (annuaireId, fallbackContact) => {
      const contact = contactsParId.get(annuaireId) || fallbackContact;
      return contact ? `${contact.nom || ""} ${contact.prenoms || ""}`.trim() : "";
    };
    const collator = new Intl.Collator();

    return [
      ...Object.values(groupedClients).map((group) => ({
        type: "group",
        name: libelleGroupe(group.annuaireId, group.clients[0]?.annuaires?.[0]),
        annuaireId: group.annuaireId,
        clients: group.clients,
      })),
      ...clientsSansGroupement.map((client) => ({
        type: "client",
        name: client.nom,
        client,
      })),
    ].sort((a, b) => collator.compare(a.name.trim(), b.name.trim()));
  }, [groupedClients, clientsSansGroupement, annuaireData]);

  const totalPages = Math.ceil(allItems.length / PAGE_SIZE) || 1;
  const safePage = currentPage - 1; // 0-indexed for UI
  const currentPageItems = allItems.slice(
    safePage * PAGE_SIZE,
    safePage * PAGE_SIZE + PAGE_SIZE
  );

  // Modifier le groupement = modifier le contact Annuaire correspondant
  // (nom/prénom, etc.) via le même formulaire que dans Annuaire.js.
  const openEditGroupementContact = (annuaireId) => {
    setEditingGroupementContactId(annuaireId);
  };

  const handleGroupementContactUpdated = (updated) => {
    if (editingGroupementContactId != null && updated) {
      updateAnnuaireContact(editingGroupementContactId, updated);
    }
    setEditingGroupementContactId(null);
    showSuccessToast(
      "Contact modifié",
      `${updated?.nom || ""} ${updated?.prenoms || ""}`.trim() ||
        "Les modifications ont été enregistrées."
    );
  };

  // Coche / décoche une valeur pour une colonne
  const handleColumnCheckboxChange = (column, value) => {
    setColumnFilters((prev) => {
      const current = prev[column] || [];
      if (current.includes(value)) {
        return { ...prev, [column]: current.filter((v) => v !== value) };
      } else {
        return { ...prev, [column]: [...current, value] };
      }
    });
  };

  // Réinitialiser le filtre d'une colonne
  const handleResetColumnFilter = (column) => {
    setColumnFilters((prev) => ({ ...prev, [column]: [] }));
  };

  const FILTERABLE_COLUMNS = {
    formeSociale: {
      label: "Forme Sociale",
      position: { top: "32.5%", left: "30%" },
    },
    dateCloture: {
      label: "Date Clôture",
      position: { top: "32.5%", left: "32%" },
    },
    regimeFiscal: {
      label: "Régime Fiscal",
      position: { top: "32.5%", left: "35%" },
    },
    regimeTVA: {
      label: "TVA",
      position: { top: "32.5%", left: "38%" },
    },
    taxeSalaires: {
      label: "Taxe salariale",
      position: { top: "32.5%", left: "41%" },
    },
    expertComptable: {
      label: "Exp Comptable",
      position: { top: "32.5%", left: "44%" },
    },
    collaborateur: {
      label: "Collab Niv 1",
      position: { top: "32.5%", left: "47%" },
    },
    collaborateur_niv2: {
      label: "Collab Niv 2",
      position: { top: "32.5%", left: "50%" },
    },

    // ✅ MISSIONS (AJOUT)
    missionComptable: {
      label: "Comptable",
      position: { top: "32.5%", left: "53%" },
    },
    missionSociale: {
      label: "Sociale",
      position: { top: "32.5%", left: "56%" },
    },
    missionJuridique: {
      label: "Juridique",
      position: { top: "32.5%", left: "59%" },
    },
    plateformeElectronique: {
      label: "PA",
      position: { top: "32.5%", left: "62%" },
    },
  };

  const FilterableTh = ({ columnKey }) => {
    const column = FILTERABLE_COLUMNS[columnKey];

    return (
      <th className="px-2 py-2.5 text-white/90 font-normal relative">
        <div className="flex items-center justify-center gap-0.5 leading-none uppercase tracking-wide">
          {column.label}
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setFilterPosition({ top: rect.bottom + 4, left: rect.left });
              setActiveFilterColumn(
                activeFilterColumn === columnKey ? null : columnKey
              );
            }}
            className="hover:bg-white/20 transition-colors rounded px-0.5 py-0.5 bg-white/10"
          >
            <ChevronDown className="w-3 h-3 text-white/90" />
          </button>
        </div>
      </th>
    );
  };

  useEffect(() => {
    setSearchTerm("");
  }, [activeFilterColumn]);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [nameFilter, columnFilters, showArchived, showBlocked]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setActiveFilterColumn(null);
      }
    };

    if (activeFilterColumn) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeFilterColumn]);

  // Ouvert par défaut : un groupe n'est replié que s'il a été fermé
  // explicitement (false). Pas de préremplissage à true par groupe — au
  // tout premier rendu, `clients` (React Query) est encore vide, donc ça ne
  // fonctionnait pas : les groupes restaient fermés tant qu'on ne cliquait
  // pas dessus.
  const [openGroups, setOpenGroups] = useState({});

  const toggleGroup = (annuaireId) => {
    setOpenGroups((prev) => ({
      ...prev,
      [annuaireId]: !(prev[annuaireId] ?? true),
    }));
  };

  const handleExport = () => {
    if (selectedClients.length <= 0) {
      setShowAlert(true);
      return;
    }
  };

  const activeFilters = Object.entries(columnFilters).filter(
    ([, values]) => values && values.length > 0
  );

  const activeFilterCount = activeFilters.length;

  const handleClearAllFilters = () => {
    setColumnFilters({});
  };

  const isGroupFullySelected = (clients) =>
    clients.every((c) => selectedClients.includes(c.id));

  const isGroupPartiallySelected = (clients) =>
    clients.some((c) => selectedClients.includes(c.id)) &&
    !isGroupFullySelected(clients);

  const toggleGroupCheckbox = (clients) => {
    const clientIds = clients.map((c) => c.id);

    if (isGroupFullySelected(clients)) {
      // Tout décocher
      setSelectedClients((prev) =>
        prev.filter((id) => !clientIds.includes(id))
      );
    } else {
      // Tout cocher
      setSelectedClients((prev) =>
        Array.from(new Set([...prev, ...clientIds]))
      );
    }
  };

  return (
    <div>
      <div>
        {showMissionPopup && selectedClients.length === 1 && (
          <Mission
            client={clients.find((c) => c.id === selectedClients[0])}
            onClose={() => setShowMissionPopup(false)}
          />
        )}

        {showNoteRDVPopup && selectedClients.length === 1 && (
          <NoteRDV
            client={clients.find((c) => c.id === selectedClients[0])}
            onClose={() => setShowNoteRDVPopup(false)}
          />
        )}

        {/* Titre */}
        <div className="mb-5">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold text-gray-900 m-0">
              Portefeuille Clients
            </h1>
            <div className="bg-[#9B1B5D]/80 text-white/90 text-xs px-1.5 py-1.5 rounded-full font-medium flex items-center justify-center leading-none">
              {nbTitre || ""}
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Vue d’ensemble du portefeuille clients
          </p>
        </div>

        {/* Barre recherche + actions */}
        <div className="flex items-center justify-between gap-4 mb-5">
          <div className="relative flex-1 max-w-xs">
            <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-8 h-8 w-full rounded-full bg-white border border-gray-200 text-xs focus:outline-none focus:border-gray-300"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowArchived(!showArchived);
                setShowBlocked(false);
              }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium transition-colors ${
                showArchived
                  ? "bg-gray-100 text-gray-700 border border-gray-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Archive className="w-3 h-3" />
              {showArchived ? "Liste" : "Archives"}
            </button>
            <button
              onClick={() => {
                setShowBlocked(!showBlocked);
                setShowArchived(false);
              }}
              className={`flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-medium transition-colors ${
                showBlocked
                  ? "bg-red-50 text-red-700 border border-red-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Ban className="w-3 h-3" />
              {showBlocked ? "Liste" : "Bloqués"}
            </button>
            <div onClick={handleExport}>
              <ExportToExcel
                data={selectedClientData}
                fileName="Clients Selectionnés"
              />
            </div>
            <button
              onClick={() => setShowCreateOverlay(true)}
              className="flex items-center gap-1.5 h-8 px-4 rounded-lg text-xs font-semibold text-white bg-[#840040] hover:bg-[#6c0034] transition-colors"
            >
              <Plus className="w-3 h-3" />
              Ajouter un dossier
            </button>
          </div>
        </div>

        {showAlert && (
          <div className="fixed top-15 right-[5%] z-50 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg shadow-lg animate-in fade-in slide-in-from-top-2">
            <AlertTriangle className="w-6 h-6 mt-0.5 text-red-600" />
            <div className="flex-1">
              <p className="font-semibold">Action impossible</p>
              <p className="text-sm">
                Veuillez sélectionner des clients avant d’exporter le tableau.
              </p>
            </div>
            <button onClick={() => setShowAlert(false)}>
              <X className="w-5 h-5 text-red-500 hover:text-red-700" />
            </button>
          </div>
        )}

        {activeFilterCount > 0 && (
          <div className="w-full mt-4 px-3 py-2 flex items-center gap-5 text-xs text-slate-700">
            {/* 🧮 Infos filtres */}
            <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
              <span className="font-medium px-3 py-2 bg-slate-50 border border-slate-200 rounded-md ">
                {activeFilterCount} filtre{activeFilterCount > 1 ? "s" : ""}{" "}
                actif{activeFilterCount > 1 ? "s" : ""}
              </span>

              {activeFilters.map(([key, values]) => (
                <span key={key} className="text-slate-600">
                  <strong>{FILTERABLE_COLUMNS[key]?.label} :</strong>{" "}
                  {values.join(", ")}
                </span>
              ))}
            </div>

            {/* ❌ Tout effacer */}
            <button
              onClick={handleClearAllFilters}
              className="text-slate-500 hover:text-[#9B1B5D] text-xs transition-colors  px-3 py-2 bg-slate-50 border border-slate-200 rounded-md"
            >
              Tout effacer
            </button>
          </div>
        )}

        {clientsLoading && clients.length === 0 ? (
          // Skeleton calqué sur le vrai tableau : même cadre, même en-tête
          // (vrais libellés), mêmes colonnes, paddings et formes de cellules
          // (barre de texte, pastilles de mission, carré GI/LAB, logo PA) pour
          // qu'il n'y ait aucun saut visuel à l'arrivée des données.
          <div className="mt-4 w-full overflow-x-auto relative">
            <table
              className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
              style={{ fontSize: "0.68rem" }}
            >
              <thead>
                <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                  <th className="w-8 px-1.5 py-1.5 text-left">
                    <div className="h-3 w-3 rounded border border-white/50" />
                  </th>
                  <th className="px-2 py-2.5 text-white/90 font-normal text-left">
                    NOM
                  </th>
                  {SKELETON_COLONNES_AVANT_GILAB.map((key) => (
                    <th
                      key={key}
                      className="px-2 py-2.5 text-white/90 font-normal relative"
                    >
                      <div className="flex items-center justify-center gap-0.5 leading-none uppercase tracking-wide">
                        {FILTERABLE_COLUMNS[key].label}
                        <span className="rounded px-0.5 py-0.5 bg-white/10">
                          <ChevronDown className="w-3 h-3 text-white/90" />
                        </span>
                      </div>
                    </th>
                  ))}
                  <th className="px-2 py-2.5 text-white/90 font-normal text-center">
                    GI&nbsp;/&nbsp;LAB
                  </th>
                  <th className="px-2 py-2.5 text-white/90 font-normal relative">
                    <div className="flex items-center justify-center gap-0.5 leading-none uppercase tracking-wide">
                      {FILTERABLE_COLUMNS.plateformeElectronique.label}
                      <span className="rounded px-0.5 py-0.5 bg-white/10">
                        <ChevronDown className="w-3 h-3 text-white/90" />
                      </span>
                    </div>
                  </th>
                  {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                    utilisateur.role
                  ) && (
                    <th className="px-2 py-2.5 text-white/90 font-normal text-right">
                      PONDÉRATION
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {Array.from({ length: 14 }).map((_, rowIdx) => {
                  const w = (liste) => liste[rowIdx % liste.length];
                  return (
                    <tr key={rowIdx} className="border-b border-slate-100">
                      <td className="px-1.5 py-2.5">
                        <div className="h-3 w-3 rounded bg-slate-200 animate-pulse" />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([150, 118, 176, 98, 140, 126, 164, 108])} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([34, 52, 30, 44])} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([46, 40, 46, 52])} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([64, 80, 56, 72])} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([38, 56, 38, 48])} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={22} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={22} />
                      </td>
                      <td className="px-2 py-2.5">
                        <SkBar w={w([22, 0, 22])} />
                      </td>
                      {[0, 1, 2].map((i) => (
                        <td key={i} className="px-2 py-2.5 text-center">
                          <div className="flex justify-center">
                            <div className="w-2 h-2 rounded-full bg-slate-200 animate-pulse" />
                          </div>
                        </td>
                      ))}
                      <td className="px-2 py-2.5 text-center">
                        <div className="flex justify-center">
                          <div className="w-6 h-6 rounded-md bg-slate-200 animate-pulse" />
                        </div>
                      </td>
                      <td className="px-2 py-2.5 text-center">
                        <div className="flex justify-center">
                          <div className="h-5 w-12 rounded bg-slate-200 animate-pulse" />
                        </div>
                      </td>
                      {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                        utilisateur.role
                      ) && (
                        <td className="px-2 py-2.5">
                          <div className="flex justify-end">
                            <SkBar w={w([26, 34, 26])} />
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pied de pagination (même mise en page que le vrai) */}
            <div className="flex items-center justify-between mt-4 px-1">
              <div className="h-3 w-44 rounded bg-slate-200 animate-pulse" />
              <div className="flex items-center gap-1.5">
                <div className="h-7 w-[74px] rounded-md border border-slate-200 bg-white" />
                <div className="h-7 w-7 rounded-md bg-slate-200 animate-pulse" />
                <div className="h-7 w-7 rounded-md border border-slate-200 bg-white" />
                <div className="h-7 w-7 rounded-md border border-slate-200 bg-white" />
                <div className="h-7 w-[58px] rounded-md border border-slate-200 bg-white" />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 w-full overflow-x-auto relative">
            {clientsFetching && (
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#f0d6e0] overflow-hidden rounded z-10">
                <div
                  className="h-full bg-[#840040]"
                  style={{
                    width: "40%",
                    animation: "progress 1.2s ease-in-out infinite",
                  }}
                />
              </div>
            )}
            <table
              className="border border-slate-200 w-full rounded-lg shadow-sm overflow-hidden bg-white"
              style={{ fontSize: "0.68rem" }}
            >
              <thead>
                <tr className="bg-gradient-to-r from-[#7A1533] to-[#5f1028]">
                  <th className="w-8 px-1.5 py-1.5 text-left">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        filteredClients.length > 0 &&
                        filteredClients.every((client) =>
                          selectedClients.includes(client.id)
                        )
                      }
                      className="h-3 w-3 rounded border-white/50 accent-white cursor-pointer"
                    />
                  </th>

                  <th className="px-2 py-2.5 text-white/90 font-normal text-left">
                    NOM
                  </th>

                  <FilterableTh columnKey="formeSociale" />
                  <FilterableTh columnKey="dateCloture" />
                  <FilterableTh columnKey="regimeFiscal" />
                  <FilterableTh columnKey="regimeTVA" />
                  <FilterableTh columnKey="expertComptable" />
                  <FilterableTh columnKey="collaborateur" />
                  <FilterableTh columnKey="collaborateur_niv2" />
                  <FilterableTh columnKey="missionComptable" />
                  <FilterableTh columnKey="missionSociale" />
                  <FilterableTh columnKey="missionJuridique" />

                  <th className="px-2 py-2.5 text-white/90 font-normal text-center">
                    GI&nbsp;/&nbsp;LAB
                  </th>

                  <FilterableTh columnKey="plateformeElectronique" />

                  {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                    utilisateur.role
                  ) && (
                    <th className="px-2 py-2.5 text-white/90 font-normal text-right">
                      PONDÉRATION
                    </th>
                  )}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200">
                {currentPageItems.map((item, index) =>
                      item.type === "group" ? (
                        // Affichage d'un groupement
                        <React.Fragment key={`group-${item.name}-${index}`}>
                          <tr>
                            <td
                              colSpan="17"
                              className="px-2 py-2.5 font-semibold uppercase text-slate-600 text-[0.68rem] tracking-wide relative bg-slate-50 border-b border-slate-200"
                            >
                              <div className="flex items-center gap-2">
                                {/* Checkbox groupement */}
                                <input
                                  type="checkbox"
                                  checked={isGroupFullySelected(item.clients)}
                                  ref={(el) => {
                                    if (el)
                                      el.indeterminate =
                                        isGroupPartiallySelected(item.clients);
                                  }}
                                  onChange={() =>
                                    toggleGroupCheckbox(item.clients)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                                />
                                {/* Chevron */}
                                <button
                                  onClick={() => toggleGroup(item.annuaireId)}
                                  className="flex items-center"
                                >
                                  <ChevronDown
                                    size={14}
                                    className={`transition-transform duration-200 ${
                                      (openGroups[item.annuaireId] ?? true)
                                        ? "rotate-0"
                                        : "-rotate-90"
                                    }`}
                                  />
                                </button>
                                {/* Nom + compteur */}
                                <span>
                                  {item.name}{" "}
                                  <span className="text-slate-500 normal-case">
                                    ({item.clients.length})
                                  </span>
                                </span>
                              </div>

                              {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                                utilisateur.role
                              ) && (
                                <button
                                  onClick={() =>
                                    openEditGroupementContact(item.annuaireId)
                                  }
                                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9B1B5D] hover:opacity-80"
                                >
                                  <GoPencil size={14} />
                                </button>
                              )}
                            </td>
                          </tr>

                          {/* Lignes des clients du groupement */}
                          {(openGroups[item.annuaireId] ?? true) &&
                            item.clients.map((client) => (
                              <tr
                                key={client.id}
                                className={`hover:bg-slate-50 transition-colors border-b border-slate-100 group ${classeLigneStatut(client.status)}`}
                                onClick={() => handleSelectClient(client)}
                              >
                                <td className="px-1.5 py-2.5">
                                  <input
                                    type="checkbox"
                                    onChange={() =>
                                      handleCheckboxChange(client.id)
                                    }
                                    checked={selectedClients.includes(
                                      client.id
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-3 w-3 rounded border-slate-300 cursor-pointer"
                                  />
                                </td>

                                <td className="px-2 py-2.5 font-bold text-slate-900 cursor-pointer hover:text-[#7A1533] transition-colors">
                                  <div className="flex items-center gap-1.5">
                                    <span>{client.nom}</span>
                                    {estSecret(client) && <MarqueSecret />}
                                    <BadgeStatut status={client.status} />
                                  </div>
                                </td>
                                <td className="px-2 py-2.5 text-slate-900 font-semibold">
                                  {Array.isArray(client.formeSociale)
                                    ? client.formeSociale.join(", ")
                                    : "—"}
                                </td>
                                <td className="px-2 py-2.5 text-slate-900 font-semibold">
                                  {formatDateWithoutYear(client.dateCloture)}
                                </td>
                                <td className="px-2 py-2.5 text-slate-900 font-semibold">
                                  {client.regimeFiscal}
                                </td>
                                <td className="px-2 py-2.5 text-slate-900 font-semibold">
                                  {client.regimeTVA}
                                </td>
                                <td
                                  className="px-2 py-2.5 text-slate-900 font-semibold"
                                  title={client.expertComptable}
                                >
                                  {getInitials(client.expertComptable)}
                                </td>
                                <td
                                  className="px-2 py-2.5 text-slate-900 font-medium"
                                  title={client.collaborateur}
                                >
                                  {getInitials(client.collaborateur)}
                                </td>
                                <td
                                  className="px-2 py-2.5 text-slate-900 font-medium"
                                  title={client.collaborateur_niv2}
                                >
                                  {getInitials(client.collaborateur_niv2)}
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                  <div className="flex justify-center">
                                    {client.missionComptable === "oui" ? (
                                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    ) : (
                                      <div className="w-2 h-2 rounded-full bg-slate-200" />
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                  <div className="flex justify-center">
                                    {client.missionSociale === "oui" ? (
                                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    ) : (
                                      <div className="w-2 h-2 rounded-full bg-slate-200" />
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-2.5 text-center">
                                  <div className="flex justify-center">
                                    {client.missionJuridique === "oui" ? (
                                      <div className="w-2 h-2 rounded-full bg-emerald-500" />
                                    ) : (
                                      <div className="w-2 h-2 rounded-full bg-slate-200" />
                                    )}
                                  </div>
                                </td>
                                <td
                                  className="px-2 py-2.5 text-center"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    onClick={() => {
                                      setGiLabClient(client);
                                      setShowGiLabOverlay(true);
                                    }}
                                    onPointerEnter={chargerGiLabOverlay}
                                    className="flex justify-center w-full"
                                  >
                                    <GiLabIcon etat={giLabProgress[client.id]} />
                                  </button>
                                </td>
                                <td
                                  className="px-2 py-2.5 text-center text-slate-700 font-medium cursor-pointer hover:bg-slate-100 transition-colors"
                                  title="Voir/modifier la plateforme électronique"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectClientPlateforme(client);
                                  }}
                                >
                                  {client.plateformeElectronique ? (
                                    <PlateformeLogo value={client.plateformeElectronique} />
                                  ) : (
                                    <span className="text-slate-300">—</span>
                                  )}
                                </td>
                                {[
                                  "ADMINISTRATEUR",
                                  "EXPERT_COMPTABLE",
                                ].includes(utilisateur.role) && (
                                  <td className="px-2 py-2.5 font-semibold text-slate-700 text-right">
                                    {client.ponderation}
                                  </td>
                                )}
                              </tr>
                            ))}
                        </React.Fragment>
                      ) : (
                        // Affichage d'un client sans groupement
                        <tr
                          key={`client-${item.client.id}-${index}`}
                          className={`hover:bg-slate-50 transition-colors border-b border-slate-100 ${classeLigneStatut(item.client.status)}`}
                          onClick={() => handleSelectClient(item.client)}
                        >
                          <td className="px-1.5 py-2.5">
                            <input
                              type="checkbox"
                              onChange={() =>
                                handleCheckboxChange(item.client.id)
                              }
                              checked={selectedClients.includes(item.client.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-3 w-3 rounded border-slate-300 cursor-pointer"
                            />
                          </td>
                          <td className="px-2 py-2.5 font-bold text-slate-900 cursor-pointer hover:text-[#7A1533] transition-colors">
                            <div className="flex items-center gap-1.5">
                              <span>{item.client.nom}</span>
                              {estSecret(item.client) && <MarqueSecret />}
                              <BadgeStatut status={item.client.status} />
                            </div>
                          </td>
                          <td className="px-2 py-2.5 text-slate-500 font-medium">
                            {Array.isArray(item.client.formeSociale)
                              ? item.client.formeSociale.join(", ")
                              : "—"}
                          </td>
                          <td className="px-2 py-2.5 text-slate-500 font-medium">
                            {formatDateWithoutYear(item.client.dateCloture)}
                          </td>
                          <td className="px-2 py-2.5 text-slate-500 font-medium">
                            {item.client.regimeFiscal}
                          </td>
                          <td className="px-2 py-2.5 text-slate-500 font-medium">
                            {item.client.regimeTVA}
                          </td>
                          <td
                            className="px-2 py-2.5 text-slate-500 font-medium"
                            title={item.client.expertComptable}
                          >
                            {getInitials(item.client.expertComptable)}
                          </td>
                          <td
                            className="px-2 py-2.5 text-slate-500 font-medium"
                            title={item.client.collaborateur}
                          >
                            {getInitials(item.client.collaborateur)}
                          </td>
                          <td
                            className="px-2 py-2.5 text-slate-500 font-medium"
                            title={item.client.collaborateur_niv2}
                          >
                            {getInitials(item.client.collaborateur_niv2)}
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <div className="flex justify-center">
                              {item.client.missionComptable === "oui" ? (
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <div className="flex justify-center">
                              {item.client.missionSociale === "oui" ? (
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2.5 text-center">
                            <div className="flex justify-center">
                              {item.client.missionJuridique === "oui" ? (
                                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                              ) : (
                                <div className="w-2 h-2 rounded-full bg-slate-200" />
                              )}
                            </div>
                          </td>
                          <td
                            className="px-2 py-2.5 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setGiLabClient(item.client);
                                setShowGiLabOverlay(true);
                              }}
                              onPointerEnter={chargerGiLabOverlay}
                              className="flex justify-center w-full"
                            >
                              <GiLabIcon etat={giLabProgress[item.client.id]} />
                            </button>
                          </td>
                          <td
                            className="px-2 py-2.5 text-center text-slate-500 font-medium cursor-pointer hover:bg-slate-100 transition-colors"
                            title="Voir/modifier la plateforme électronique"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectClientPlateforme(item.client);
                            }}
                          >
                            {item.client.plateformeElectronique ? (
                              <PlateformeLogo value={item.client.plateformeElectronique} />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          {["ADMINISTRATEUR", "EXPERT_COMPTABLE"].includes(
                            utilisateur.role
                          ) && (
                            <td className="px-2 py-2.5 font-semibold text-slate-700 text-right">
                              {item.client.ponderation}
                            </td>
                          )}
                        </tr>
                      )
                    )}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 px-1">
                <span className="text-xs text-slate-500">
                  Page {currentPage} sur {totalPages} — {filteredClients.length}{" "}
                  clients
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage <= 1}
                    className="h-7 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Précédent
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => {
                    const show =
                      totalPages <= 7 ||
                      i === 0 ||
                      i === totalPages - 1 ||
                      Math.abs(i - safePage) <= 1;
                    const showEllipsisBefore =
                      i === safePage - 2 && safePage - 2 > 1;
                    const showEllipsisAfter =
                      i === safePage + 2 && safePage + 2 < totalPages - 2;
                    if (!show) return null;
                    return (
                      <React.Fragment key={i}>
                        {showEllipsisBefore && (
                          <span className="text-xs text-slate-400 px-1">…</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(i + 1)}
                          className={`h-7 w-7 rounded-md text-xs font-medium transition-colors ${
                            i === safePage
                              ? "bg-[#840040] text-white"
                              : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {i + 1}
                        </button>
                        {showEllipsisAfter && (
                          <span className="text-xs text-slate-400 px-1">…</span>
                        )}
                      </React.Fragment>
                    );
                  })}
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages}
                    className="h-7 px-3 rounded-md text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {activeFilterColumn &&
          (() => {
            const filteredValues = (
              uniqueValues[activeFilterColumn] || []
            ).filter((v) =>
              String(v).toLowerCase().includes(searchTerm.toLowerCase())
            );
            const selectedInColumn = columnFilters[activeFilterColumn] || [];
            const allChecked =
              filteredValues.length > 0 &&
              filteredValues.every((v) => selectedInColumn.includes(v));
            const someChecked = filteredValues.some((v) =>
              selectedInColumn.includes(v)
            );

            const handleSelectAll = () => {
              if (allChecked) {
                setColumnFilters((prev) => ({
                  ...prev,
                  [activeFilterColumn]: [],
                }));
              } else {
                setColumnFilters((prev) => ({
                  ...prev,
                  [activeFilterColumn]: filteredValues,
                }));
              }
            };

            return (
              <div
                ref={filterRef}
                className="fixed z-[10000] bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden"
                style={{
                  top: filterPosition.top,
                  left: filterPosition.left,
                  width: "230px",
                  maxHeight: "300px",
                }}
              >
                {/* Recherche */}
                <div className="px-3 pt-3 pb-2 border-b border-slate-100">
                  <div className="relative">
                    <IoIosSearch
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                      size={14}
                    />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-7 pr-2 py-1.5 rounded-md border border-slate-200 text-xs focus:outline-none focus:ring-1 focus:ring-[#7A1533]/40"
                    />
                  </div>
                </div>

                {/* Tout sélectionner */}
                <div className="px-3 py-1.5 border-b border-slate-100 bg-slate-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = !allChecked && someChecked;
                      }}
                      onChange={handleSelectAll}
                      className="h-3 w-3 rounded accent-[#7A1533]"
                    />
                    <span className="text-xs text-slate-600 font-medium">
                      Tout sélectionner...
                    </span>
                    <span className="ml-auto text-xs text-slate-400">
                      {filteredValues.length}
                    </span>
                  </label>
                </div>

                {/* Liste */}
                <div className="overflow-y-auto flex-1 px-3 py-1.5">
                  {filteredValues.length === 0 ? (
                    <p className="text-xs text-slate-400 py-2">
                      Aucun résultat
                    </p>
                  ) : (
                    filteredValues.map((value) => (
                      <label
                        key={value}
                        className="flex items-center gap-2 py-1 px-1 rounded cursor-pointer hover:bg-slate-50 text-xs text-slate-700"
                      >
                        <input
                          type="checkbox"
                          checked={selectedInColumn.includes(value)}
                          onChange={() =>
                            handleColumnCheckboxChange(
                              activeFilterColumn,
                              value
                            )
                          }
                          className="h-3 w-3 rounded accent-[#7A1533]"
                        />
                        {value}
                      </label>
                    ))
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center px-3 py-2 border-t border-slate-100">
                  <button
                    onClick={() => handleResetColumnFilter(activeFilterColumn)}
                    className="text-xs px-3 py-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 font-medium transition-colors"
                  >
                    Réinitialiser
                  </button>
                  <button
                    onClick={() => setActiveFilterColumn(null)}
                    className="text-xs px-3 py-1.5 rounded-md bg-[#7A1533] hover:bg-[#5f1028] text-white/90 font-semibold transition-colors"
                  >
                    Appliquer
                  </button>
                </div>
              </div>
            );
          })()}

        {showGiLabOverlay && giLabClient && (
          <Suspense
            fallback={
              <GiLabOverlaySkeleton
                clientNom={giLabClient.nom || ""}
                onClose={() => setShowGiLabOverlay(false)}
              />
            }
          >
          <GiLabOverlay
            client={giLabClientLive}
            onClose={() => setShowGiLabOverlay(false)}
            onOpenDetail={() => {
              setShowGiLabOverlay(false);
              setSelectedClientId(giLabClient.id);
              setOverlayInitialTab("gi-lab");
              setShowOverlay(true);
            }}
            onOpenFiche={() => {
              setShowGiLabOverlay(false);
              setSelectedClientId(giLabClient.id);
              setOverlayInitialTab("general");
              setShowOverlay(true);
            }}
            onOverallChange={(etat) =>
              setGiLabProgress((prev) => ({ ...prev, [giLabClient.id]: etat }))
            }
            onTodoToggled={(taskId, checked) =>
              setGiLabClient((prev) => ({
                ...prev,
                todos: (prev?.todos || []).map((t) =>
                  t.id === taskId
                    ? { ...t, status: checked ? "Terminer" : "En cours" }
                    : t
                ),
              }))
            }
          />
          </Suspense>
        )}

        {editingGroupementContactId && (
          <FormOverlay onClose={() => setEditingGroupementContactId(null)}>
            <NouveauContactForm
              contact={annuaireData.find(
                (a) => a.id === editingGroupementContactId
              )}
              contactId={editingGroupementContactId}
              onContactCreated={handleGroupementContactUpdated}
              onBack={() => setEditingGroupementContactId(null)}
              onCancel={() => setEditingGroupementContactId(null)}
            />
          </FormOverlay>
        )}

        <FicheClientOverlay
          client={selectedClient}
          open={showOverlay}
          loading={detailLoading}
          timelineLoading={
            detailFetching && selectedClient?.historiques === undefined
          }
          coreLoading={
            detailFetching && selectedClient?.comptes === undefined
          }
          extrasLoading={
            detailFetching && selectedClient?.Evenement === undefined
          }
          initialTab={overlayInitialTab}
          onClose={() => setShowOverlay(false)}
          onClientUpdated={handleUpdateUserInList}
        />
        {selectedClients.length > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <div className="flex items-center bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
              <div className="bg-[#840040] text-white/90 px-5 py-4 text-sm font-bold flex items-center justify-center min-w-[56px]">
                <p>{selectedClients.length}</p>
              </div>
              <div className="flex items-center px-4 gap-6">
                <p className="text-sm font-medium text-slate-700">
                  Dossier(s) sélectionné(s)
                </p>
                <div
                  className="flex flex-col items-center cursor-pointer text-slate-600 hover:text-[#840040] transition-colors"
                  onClick={handleDeleteSelected}
                >
                  <AiOutlineDelete size={17.5} />
                  <p className="text-xs font-bold mt-1">
                    {isDeleting ? "Suppression..." : "Supprimer"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      {showCreateOverlay && (
        <div className="overlayAC">
          <div className="inoverlayLead">
            <Create onCancel={() => setShowCreateOverlay(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

export default Suivi;
